import { useState, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Shared hook for fetching order routing suggestions.
 * Used by OrderForm and PendingBeamBeforePurchases.
 *
 * fetchRoutingSuggestion({ website, amount, isAir, customerId, paymentMethod })
 *   → calls POST /api/order/routing-suggestion
 *   → stores result in `routingSuggestion`
 *   → returns the suggestion (or null on error / missing amount)
 */
const useRoutingSuggestion = () => {
  const [routingSuggestion, setRoutingSuggestion] = useState(null);

  const fetchRoutingSuggestion = useCallback(async ({
    website = null,
    amount,
    isAir = false,
    customerId = null,
    paymentMethod = 'any',
    signal,
  } = {}) => {
    const parsedAmount = parseFloat(amount);
    if (!parsedAmount || isNaN(parsedAmount)) {
      setRoutingSuggestion(null);
      return null;
    }

    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/order/routing-suggestion`,
        {
          website: website || null,
          amount: parsedAmount,
          isAir: isAir || false,
          customerId: customerId || null,
          paymentMethod: paymentMethod || 'any',
        },
        signal ? { signal } : {}
      );
      setRoutingSuggestion(res.data);
      return res.data;
    } catch (err) {
      if (err.name !== 'CanceledError' && err.name !== 'AbortError') {
        console.error('Routing suggestion failed:', err);
      }
      return null;
    }
  }, []);

  const clearRoutingSuggestion = useCallback(() => setRoutingSuggestion(null), []);

  return { routingSuggestion, fetchRoutingSuggestion, clearRoutingSuggestion };
};

export default useRoutingSuggestion;
